import React from 'react';
import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%   { opacity: 0.6; transform: scale(0.93); }
  50%  { opacity: 1;   transform: scale(1); }
  100% { opacity: 0.6; transform: scale(0.93); }
`;

const dotFade = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
  40%            { opacity: 1;   transform: scale(1); }
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 20px;
`;

const Ring = styled.div`
  position: relative;
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 3px solid rgba(255, 255, 255, 0.08);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 3px solid transparent;
    border-top-color: rgba(102, 126, 234, 0.9);
    border-right-color: rgba(118, 75, 162, 0.45);
    animation: ${spin} 1s linear infinite;
  }
`;

const FlagWrap = styled.div`
  animation: ${pulse} 2s ease-in-out infinite;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.5));
  border-radius: 4px;
  overflow: hidden;
  line-height: 0;
`;

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.95em;
  font-weight: 500;
  letter-spacing: 0.3px;
`;

const Dots = styled.div`
  display: flex;
  gap: 5px;
  margin-top: -8px;

  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(102, 126, 234, 0.8);
    display: inline-block;
    animation: ${dotFade} 1.2s ease-in-out infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

/* Union Jack — inline SVG, no external references, public domain */
const UKFlag = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="54" height="27" aria-hidden="true">
    {/* Blue field */}
    <rect width="60" height="30" fill="#012169" />

    {/* St Andrew's cross — white diagonals */}
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="white" strokeWidth="6" />

    {/* St Patrick's cross — red counter-changed diagonals */}
    {/* Upper-left & lower-right: red stripe offset toward top-right */}
    <path d="M0,0 L30,15" stroke="#C8102E" strokeWidth="2" strokeDasharray="2.2,0" transform="translate(1.5,-0.75)" />
    <path d="M30,15 L60,30" stroke="#C8102E" strokeWidth="2" transform="translate(1.5,-0.75)" />
    {/* Upper-right & lower-left: red stripe offset toward bottom-right */}
    <path d="M60,0 L30,15" stroke="#C8102E" strokeWidth="2" transform="translate(-1.5,-0.75)" />
    <path d="M30,15 L0,30" stroke="#C8102E" strokeWidth="2" transform="translate(-1.5,-0.75)" />

    {/* St George's cross — white fimbriation */}
    <path d="M30,0 V30 M0,15 H60" stroke="white" strokeWidth="10" />

    {/* St George's cross — red */}
    <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);

const LoadingSpinner = ({ message = 'Loading petitions…' }) => (
  <SpinnerContainer>
    <Ring>
      <FlagWrap>
        <UKFlag />
      </FlagWrap>
    </Ring>
    <LoadingText>{message}</LoadingText>
    <Dots>
      <span /><span /><span />
    </Dots>
  </SpinnerContainer>
);

export default LoadingSpinner;
